if (!customElements.get("featured-product-refactored")) {
  customElements.define(
    "featured-product-refactored",
    class FeaturedProductRefactored extends HTMLElement {
      constructor() {
        super();
        this.parsedProducts = [];
        this.finalProductId = this.dataset.defaultProductId;
        this.sectionId = this.dataset.sectionId;
        this.formId = `featured-product-refactored-form-${this.sectionId}`;
        this.uniqueImagesOnly = this.dataset.uniqueImagesOnly === "true";
        this.sortOption = this.dataset.sortOption || "alphabetical";
        this.targets = this.initializeTargets();
        this.cachedContainers = {};
      }

      connectedCallback() {
        if (!this.isConnected) return;
        this.cacheContainers();
        if (!this.allContainersExist()) return;
        document.addEventListener(
          "change",
          this.debounce(this.handleRadioChange.bind(this), 300)
        );
        document.addEventListener(
          "change",
          this.handleCheckboxChange.bind(this)
        );
        this.loadSwatches();
        this.startShippingNoticeCountdown();
        this.addKeyboardNavigation();

        // Check if there's saved state to restore (from updateContent)
        if (this._pendingStateRestore) {
          this.restoreFormState(this._pendingStateRestore);
          this._pendingStateRestore = null;
        }
      }

      disconnectedCallback() {
        document.removeEventListener("change", this.handleRadioChange);
        document.removeEventListener("change", this.handleCheckboxChange);
      }

      addKeyboardNavigation() {
        this.addEventListener("keydown", (event) => {
          const focusedElement = document.activeElement;

          if (
            focusedElement?.tagName === "LABEL" &&
            focusedElement.getAttribute("for")
          ) {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              const associatedInput = this.querySelector(
                `#${focusedElement.getAttribute("for")}`
              );
              if (associatedInput) {
                associatedInput.checked = true;
                associatedInput.dispatchEvent(
                  new Event("change", { bubbles: true })
                );
              }
            }
          }
        });
      }

      initializeTargets() {
        return {
          first: {
            selector: 'div[id^="shopify-block-first_step_"]',
            container: null,
            radioSelector: 'input[name="first_step_product"]',
          },
          addon: {
            selector: 'div[id^="shopify-block-addon_step_"]',
            container: null,
            checkboxSelector: 'input[type="checkbox"][name^="items["]',
          },
          second: {
            selector: 'div[id^="shopify-block-second_step_"]',
            container: null,
            radioSelector: 'input[name="second_step_product"]',
          },
          third: {
            selector: 'div[id^="shopify-block-third_step_"]',
            container: null,
            radioSelector: 'input[name="third_step_product"]',
          },
          fourth: {
            selector: 'div[id^="shopify-block-fourth_step_"]',
            container: null,
            radioSelector: 'input[name="properties[Gift Card Type]"]',
            detailsSelector: ".fourth-step--container",
            titleFieldSelector: 'input[name="properties[Gift Card Title]"]',
            messageFieldSelector:
              'textarea[name="properties[Gift Card Message]"]',
          },
          shipping_notice: {
            selector: 'div[id^="shopify-block-shipping_notice_"]',
            container: null,
          },
        };
      }

      cacheContainers() {
        Object.keys(this.targets).forEach((key) => {
          if (!this.cachedContainers[key]) {
            this.cachedContainers[key] = this.querySelector(
              this.targets[key].selector
            );
          }
          this.targets[key].container = this.cachedContainers[key];
        });
      }

      allContainersExist() {
        // Addon step, shipping notice, and fourth step are optional, so we don't require them to exist
        const requiredTargets = { ...this.targets };
        delete requiredTargets.addon;
        delete requiredTargets.shipping_notice;
        delete requiredTargets.fourth;
        return Object.values(requiredTargets).every(
          (target) => target.container !== null
        );
      }

      async startShippingNoticeCountdown() {
        const { shipping_notice } = this.targets;
        if (!shipping_notice.container) return;

        const fetchDuration = async () => {
          const url = `${window.location.origin}/a/proxy/storefront/shipping-notice`;
          const blockData =
            shipping_notice.container.querySelector('[id$="-data"]');

          if (!blockData) {
            console.error("Block data element not found");
            return null;
          }

          try {
            const response = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(JSON.parse(blockData.innerHTML)),
            });

            if (!response.ok) {
              throw new Error(`HTTP error! Status: ${response.status}`);
            }

            return await response.json();
          } catch (error) {
            console.error("Error fetching duration:", error);
            return null;
          }
        };

        const updateTimeElements = (container, hours, minutes, seconds) => {
          const hoursSpan = container.querySelector(
            ".shipping-notice--text .hours"
          );
          const minutesSpan = container.querySelector(
            ".shipping-notice--text .minutes"
          );
          const secondsSpan = container.querySelector(
            ".shipping-notice--text .seconds"
          );

          if (hoursSpan)
            hoursSpan.textContent = hours.toString().padStart(2, "0");
          if (minutesSpan)
            minutesSpan.textContent = minutes.toString().padStart(2, "0");
          if (secondsSpan)
            secondsSpan.textContent = seconds.toString().padStart(2, "0");
        };

        const updateTargetDay = (container, shippingDate) => {
          const targetDaySpan = container.querySelector(
            ".shipping-notice--text .target-day"
          );
          if (!targetDaySpan) return;

          const endDate = new Date(shippingDate);
          const localEndDate = new Date(endDate.toLocaleString());
          const now = new Date();
          const tomorrow = new Date();
          tomorrow.setDate(now.getDate() + 1);

          if (now.toDateString() === localEndDate.toDateString()) {
            targetDaySpan.textContent = container.dataset.sameDay;
          } else if (tomorrow.toDateString() === localEndDate.toDateString()) {
            targetDaySpan.textContent = container.dataset.nextDay;
          } else {
            const options = { weekday: "long" };
            targetDaySpan.textContent = localEndDate.toLocaleDateString(
              undefined,
              options
            );
          }
        };

        const response = await fetchDuration();
        if (response === null) {
          console.error(
            "Failed to retrieve duration, countdown will not start."
          );
          return;
        }

        const { diffInMilliseconds, firstPossibleRate } = response;
        const shippingDate = firstPossibleRate.shipping_date;
        const container = shipping_notice.container;

        if (!container) {
          console.error("Countdown container not found");
          return;
        }

        let endTime = Date.now() + diffInMilliseconds;

        const updateCountdown = async () => {
          const remainingMs = endTime - Date.now();
          if (remainingMs <= 0) {
            clearInterval(timerInterval);
            updateTimeElements(container, 0, 0, 0);
            await this.startShippingNoticeCountdown();
            return;
          }

          const hours = Math.floor(remainingMs / (1000 * 60 * 60));
          const minutes = Math.floor(
            (remainingMs % (1000 * 60 * 60)) / (1000 * 60)
          );
          const seconds = Math.floor((remainingMs % (1000 * 60)) / 1000);

          updateTimeElements(container, hours, minutes, seconds);
          updateTargetDay(container, shippingDate);
        };

        const timerInterval = setInterval(updateCountdown, 1000);
        updateCountdown();
      }

      debounce(func, wait) {
        let timeout;
        return (...args) => {
          clearTimeout(timeout);
          timeout = setTimeout(() => func.apply(this, args), wait);
        };
      }

      handleCheckboxChange = (event) => {
        const target = event.target;
        const { addon } = this.targets;

        // Handle addon step checkbox changes (no product updates needed, just form integration)
        if (
          addon.container?.contains(target) &&
          target.matches('input[type="checkbox"][name^="items["]')
        ) {
          // Addon products are handled by form submission, no need to update product
          return;
        }
      };

      handleRadioChange = async (event) => {
        const target = event.target;
        const { first, second, third, fourth } = this.targets;
        if (
          first.container?.contains(target) &&
          target.matches(first.radioSelector)
        ) {
          const productUrl = target.dataset.productUrl;
          if (!productUrl) return;
          await this.updateContent(productUrl);
          this.scrollToTop();
          if (this.targets.second.container) {
            this.animateSelectionChange(this.targets.second.container);
          }
          if (this.targets.third.container) {
            this.animateSelectionChange(this.targets.third.container);
          }
          return;
        }

        if (
          second.container?.contains(target) &&
          target.matches(second.radioSelector)
        ) {
          const productIndex = parseInt(target.value);
          const selectedProduct = this.parsedProducts.at(productIndex);
          await this.handleSecondStepChange(selectedProduct);
          this.scrollToTop();
          return;
        }

        if (
          third.container?.contains(target) &&
          target.matches(third.radioSelector)
        ) {
          const checkedInput = this.targets.second.container?.querySelector(
            "input[name='second_step_product']:checked"
          );
          if (!checkedInput) return;
          const productIndex = checkedInput.value;
          const selectedProduct = this.parsedProducts.at(productIndex);
          const boxIndex = parseInt(target.value);
          const selectedFinalProduct = selectedProduct.boxes.at(boxIndex);
          await this.handleThirdStepChange(selectedFinalProduct);
          this.scrollToTop();
          return;
        }

        if (
          fourth.container?.contains(target) &&
          target.matches(fourth.radioSelector)
        ) {
          this.handleFourthStepChange(target);
          return;
        }
      };

      handleFourthStepChange(target) {
        const { fourth } = this.targets;
        if (!fourth.container) return;

        const { detailsSelector, titleFieldSelector, messageFieldSelector } =
          this.targets.fourth;
        const detailsElement = this.querySelector(detailsSelector);
        const titleField = this.querySelector(titleFieldSelector);
        const messageField = this.querySelector(messageFieldSelector);
        const modalButton = this.querySelector(
          ".fourth-step__about #modal-button"
        );

        if (!detailsElement || !titleField || !messageField || !modalButton)
          return;

        if (target.value === "") {
          modalButton.classList.add("hidden");
          detailsElement.setAttribute("aria-expanded", "false");
          setTimeout(() => {
            detailsElement.open = false;
          }, 500);

          titleField.disabled = true;
          messageField.disabled = true;
          titleField.required = false;
          messageField.required = false;
          this.resetFieldFormId(titleField);
          this.resetFieldFormId(messageField);
        } else if (target.value === "Million eCard") {
          modalButton.classList.remove("hidden");
          detailsElement.open = true;

          titleField.disabled = false;
          messageField.disabled = false;
          titleField.required = true;
          messageField.required = true;
          this.setFieldFormId(titleField, this.formId);
          this.setFieldFormId(messageField, this.formId);
        } else if (target.value === "Paper Card") {
          modalButton.classList.add("hidden");
          detailsElement.open = true;

          titleField.disabled = true;
          messageField.disabled = false;
          titleField.required = false;
          messageField.required = true;
          this.resetFieldFormId(titleField);
          this.setFieldFormId(messageField, this.formId);
        }
      }

      setFieldFormId(field, formId) {
        field.setAttribute("form", formId);
      }

      resetFieldFormId(field) {
        field.removeAttribute("form");
      }

      loadSwatches = async () => {
        const { first } = this.targets;
        const selectedRadio = first.container.querySelector(
          "input[name='first_step_product']:checked"
        );
        if (!selectedRadio) return;

        const boxShapeTag = selectedRadio.dataset.tag;
        if (!boxShapeTag) return;

        try {
          const products = await this.fetchSwatchData(boxShapeTag);
          this.refreshParsedProducts(products);
          this.handleSwatchesData();
        } catch (error) {
          console.error("Error fetching swatches:", error);
        }
      };

      fetchSwatchData = async (boxShapeTag) => {
        const headers = new Headers({
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            "8996d359b34dab28ad60af2c4c4f4b73",
        });
        const graphqlQuery = JSON.stringify({
          query: `query {
          products(first: 250, query: "tag:{${boxShapeTag}}") {
            edges {
              node {
                id
                handle
                flower_image_url: metafield(key: "stc_flower_image", namespace: "custom") { value }
                box_image_url: metafield(key: "stc_box_image", namespace: "custom") { value }
                flower_name: metafield(key: "stc_flower_name", namespace: "custom") { value }
                box_color: metafield(key: "stc_box_color", namespace: "custom") { value }
              }
            }
          }
        }`,
        });

        const response = await fetch(
          "https://the-million-roses-los-angeles.myshopify.com/api/2025-07/graphql.json",
          {
            method: "POST",
            headers,
            body: graphqlQuery,
          }
        );
        const data = await response.json();
        if (!data?.data?.products) {
          throw new Error("Invalid API response structure");
        }
        return data.data.products.edges;
      };

      refreshParsedProducts = (products) => {
        const uniqueFlowerImages = new Set();

        this.parsedProducts = products
          .map((product) => {
            const productNode = product.node;
            if (
              !productNode.flower_name?.value ||
              !productNode.flower_image_url?.value
            )
              return null;

            if (this.uniqueImagesOnly) {
              if (uniqueFlowerImages.has(productNode.flower_image_url.value))
                return null;
              uniqueFlowerImages.add(productNode.flower_image_url.value);
            }

            const boxes = products
              .filter(
                (item) =>
                  item.node.flower_name?.value === productNode.flower_name.value
              )
              .map((item) => {
                return {
                  id: item.node.id,
                  handle: item.node.handle,
                  box_color: item.node.box_color?.value,
                  box_image_url: item.node.box_image_url?.value,
                };
              })
              .filter((box) => box !== null);

            return {
              id: productNode.id,
              handle: productNode.handle,
              flower_color: productNode.flower_name.value,
              flower_image_url: productNode.flower_image_url.value,
              boxes,
            };
          })
          .filter((product) => product !== null);

        if (this.sortOption === "alphabetical") {
          this.parsedProducts.sort((a, b) =>
            a.flower_color.localeCompare(b.flower_color)
          );
        } else if (this.sortOption === "length") {
          this.parsedProducts.sort(
            (a, b) => a.flower_color.length - b.flower_color.length
          );
        }
      };

      handleSwatchesData = () => {
        const { first, second, third } = this.targets;
        if (!first.container || !second.container || !third.container) return;

        const selectedRadio = first.container.querySelector(
          "input[name='first_step_product']:checked"
        );
        if (!selectedRadio) return;

        const selectedFlowerColor = selectedRadio.dataset.flowerColor;
        const selectedProduct = this.parsedProducts.find(
          (product) => product.flower_color === selectedFlowerColor
        );

        const secondContainer = second.container.querySelector(
          ".second-step--container"
        );
        const thirdContainer = third.container.querySelector(
          ".third-step--container"
        );
        if (!secondContainer || !thirdContainer) return;

        this.updateFlowerColors(secondContainer, selectedFlowerColor);
        this.updateBoxColors(thirdContainer, selectedProduct);
      };

      handleSecondStepChange = async (selectedProduct) => {
        const { third } = this.targets;
        const productUrl = `/products/${selectedProduct.handle}`;
        const updatedDocument = await this.fetchUpdatedContent(productUrl);
        this.updateGallery(updatedDocument);
        this.updateHeaders(updatedDocument);
        this.updateBuyButtons(updatedDocument);

        if (third.container) {
          const thirdContainer = third.container.querySelector(
            ".third-step--container"
          );
          if (thirdContainer) {
            this.updateBoxColors(thirdContainer, selectedProduct);
          }
        }
      };

      handleThirdStepChange = async (selectedProduct) => {
        const productUrl = `/products/${selectedProduct.handle}`;
        const updatedDocument = await this.fetchUpdatedContent(productUrl);
        this.updateGallery(updatedDocument);
        this.updateBuyButtons(updatedDocument);
        this.updateThirdStepHeader();
      };

      updateFlowerColors = (container, selectedFlowerColor) => {
        const flowerColorsHTML = this.parsedProducts
          .map(
            (item, index) => `
        <label class="second-step--flower-color" for="second_step_product-${index}" tabindex="0" aria-checked="${
              item.flower_color === selectedFlowerColor
            }">
          <input 
            type="radio" 
            id="second_step_product-${index}" 
            data-product-id="${item.id}" 
            data-product-url="/products/${item.handle}" 
            name="second_step_product" 
            value="${index}" 
            ${item.flower_color === selectedFlowerColor ? "checked" : ""} 
            aria-labelledby="flower-color-label-${index}"
          >
          <img 
            src="${item.flower_image_url}" 
            width="50" 
            height="50" 
            class="second-step--second_type_product-image" 
            alt="${item.flower_color}" 
            id="flower-color-label-${index}"
          >
        </label>`
          )
          .join("");

        this.populateComponent(
          container,
          this.createFragment(flowerColorsHTML)
        );
        this.animateSelectionChange(container);
      };

      updateBoxColors = (container, parsedProduct) => {
        const boxColorsHTML = parsedProduct.boxes
          .map(
            (item, index) => `
        <label class="third-step--box-color" for="third_step_product-${index}" tabindex="0" aria-checked="${
              index === 0
            }">
          <input 
            type="radio" 
            id="third_step_product-${index}" 
            data-product-id="${item.id}" 
            name="third_step_product" 
            value="${index}" 
            data-box-color="${item.box_color}" 
            ${index === 0 ? "checked" : ""} 
            aria-labelledby="box-color-label-${index}"
          >
          <img 
            src="${item.box_image_url}" 
            width="50" 
            height="50" 
            class="third-step--third_type_product-image" 
            alt="${item.box_color}" 
            id="box-color-label-${index}"
          >
        </label>`
          )
          .join("");

        this.populateComponent(container, this.createFragment(boxColorsHTML));
        this.animateSelectionChange(container);
      };

      updateHeaders = (newContent) => {
        const { second, third } = this.targets;
        if (second.container) {
          const secondHeading = second.container.querySelector(
            ".second-step--heading"
          );
          const newSecondHeading = newContent.querySelector(
            ".second-step--heading"
          );
          if (secondHeading && newSecondHeading) {
            this.replaceComponent(secondHeading, newSecondHeading);
          }
        }
        if (third.container) {
          const thirdHeading = third.container.querySelector(
            ".third-step--heading"
          );
          const newThirdHeading = newContent.querySelector(
            ".third-step--heading"
          );
          if (thirdHeading && newThirdHeading) {
            this.replaceComponent(thirdHeading, newThirdHeading);
          }
        }
      };

      updateThirdStepHeader = () => {
        const { third } = this.targets;
        if (!third.container) return;

        const selectedRadio = third.container.querySelector(
          "input[name='third_step_product']:checked"
        );
        if (!selectedRadio) return;

        const thirdStepHeader = third.container.querySelector(
          ".third-step--heading .current_selection_header"
        );
        if (thirdStepHeader) {
          thirdStepHeader.textContent = selectedRadio.dataset.boxColor;
        }
      };

      createFragment = (html) => {
        const template = document.createElement("template");
        template.innerHTML = html;
        return template.content;
      };

      populateComponent = (container, newContent) => {
        if (container && newContent) {
          container.innerHTML = "";
          container.appendChild(newContent);
        }
      };

      replaceComponent = (container, newContent) => {
        if (container && newContent) {
          container.replaceWith(newContent);
        }
      };

      saveFormState = () => {
        const state = {
          addon: {},
          fourth: {
            radio: null,
            title: null,
            message: null,
          },
        };

        // Save addon step checkbox states
        const { addon } = this.targets;
        if (addon.container) {
          const checkboxes = addon.container.querySelectorAll(
            'input[type="checkbox"][name^="items["]'
          );
          checkboxes.forEach((checkbox) => {
            if (checkbox.checked) {
              state.addon[checkbox.name] = checkbox.value;
            }
          });
        }

        // Save fourth step state
        const { fourth } = this.targets;
        if (fourth.container) {
          const selectedRadio = fourth.container.querySelector(
            fourth.radioSelector + ":checked"
          );
          if (selectedRadio) {
            state.fourth.radio = selectedRadio.value;
          }

          const titleField = this.querySelector(fourth.titleFieldSelector);
          const messageField = this.querySelector(fourth.messageFieldSelector);
          if (titleField) {
            state.fourth.title = titleField.value;
          }
          if (messageField) {
            state.fourth.message = messageField.value;
          }
        }

        return state;
      };

      restoreFormState = (state) => {
        // Restore addon step checkbox states
        const { addon } = this.targets;
        if (addon.container && Object.keys(state.addon).length > 0) {
          const checkboxes = addon.container.querySelectorAll(
            'input[type="checkbox"][name^="items["]'
          );
          checkboxes.forEach((checkbox) => {
            if (state.addon[checkbox.name] === checkbox.value) {
              checkbox.checked = true;
            }
          });
        }

        // Restore fourth step state
        const { fourth } = this.targets;
        if (fourth.container) {
          if (state.fourth.radio !== null) {
            const radioInput = fourth.container.querySelector(
              `${fourth.radioSelector}[value="${state.fourth.radio}"]`
            );
            if (radioInput) {
              radioInput.checked = true;
              radioInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
          }

          // Restore input fields after a short delay to ensure form is ready
          setTimeout(() => {
            const titleField = this.querySelector(fourth.titleFieldSelector);
            const messageField = this.querySelector(
              fourth.messageFieldSelector
            );
            if (titleField && state.fourth.title !== null) {
              titleField.value = state.fourth.title;
            }
            if (messageField && state.fourth.message !== null) {
              messageField.value = state.fourth.message;
            }
          }, 100);
        }
      };

      updateContent = async (productUrl) => {
        this.setLoadingState(true);
        try {
          // Save current form state before replacement
          const savedState = this.saveFormState();

          const updatedDocument = await this.fetchUpdatedContent(productUrl);
          const updatedElement = updatedDocument.querySelector(
            `featured-product-refactored[data-section-id="${this.sectionId}"]`
          );
          this.replaceSelf(updatedElement);

          // Restore form state after replacement
          // Store state to be restored when new element connects
          setTimeout(() => {
            const newElement = document.querySelector(
              `featured-product-refactored[data-section-id="${this.sectionId}"]`
            );
            if (newElement) {
              newElement._pendingStateRestore = savedState;
              // Also try immediate restore in case element is already connected
              if (newElement.restoreFormState) {
                setTimeout(() => {
                  newElement.restoreFormState(savedState);
                }, 100);
              }
            }
          }, 200);
        } catch (error) {
          console.error("Error updating the section:", error);
        } finally {
          this.setLoadingState(false);
        }
      };

      updateGallery = async (updatedDocument) => {
        const updatedGallery = updatedDocument.querySelector("product-gallery");
        const galleryContainer = this.querySelector("product-gallery");
        this.replaceComponent(galleryContainer, updatedGallery);
      };

      updateBuyButtons = async (updatedDocument) => {
        const updatedBuyButtons = updatedDocument.querySelector(
          '[action="/cart/add"]'
        );
        const buyButtonsContainer = this.querySelector('[action="/cart/add"]');
        this.replaceComponent(buyButtonsContainer, updatedBuyButtons);
      };

      fetchUpdatedContent = async (productUrl) => {
        const url = new URL(location.href);
        url.searchParams.append("section_id", this.sectionId);
        url.pathname = productUrl;

        const headers = new Headers({ "Cache-Control": "max-age=3600" });
        const response = await fetch(url, { headers });
        if (!response.ok)
          throw new Error(
            `Failed to fetch updated content: ${response.statusText}`
          );
        return new DOMParser().parseFromString(
          await response.text(),
          "text/html"
        );
      };

      replaceSelf = (updatedElement) => {
        if (updatedElement) {
          this.parentNode.replaceChild(updatedElement, this);
        }
      };

      setLoadingState = (isLoading) => {
        const interactiveElements = this.querySelectorAll("input, button");
        this.classList.toggle("loading", isLoading);
        interactiveElements.forEach((el) => (el.disabled = isLoading));
      };

      scrollToTop = () => {
        const section = document.getElementById(
          `shopify-section-${this.sectionId}`
        );
        if (section) {
          section.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        } else {
          this.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
        }
      };

      animateSelectionChange = (container) => {
        if (container) {
          container.style.transition = "transform 0.3s ease, opacity 0.3s ease";
          container.style.transform = "scale(1.05)";
          container.style.opacity = "0.8";

          setTimeout(() => {
            container.style.transform = "scale(1)";
            container.style.opacity = "1";
          }, 300);
        }
      };
    }
  );
}
